import { h } from 'preact';
import { useMemo, useContext } from 'preact/compat';
import { decodeEntities } from '@wordpress/html-entities';
import { useCart } from '../hooks/cart';
import { safegaurd } from '../hooks/candidateSafeGaurds';
import Image from './Image';
import Logo from '../icons/Logo';
import type { Candidate } from '../types';
import AmountButtonGroup from './inputs/AmountButtonGroup';
import CheckSimple from '../icons/CheckSimple';
import { Theme } from '../lib/render';

export type CandidateBoxProps = {
	candidate: Candidate;
	className?: string;
	color?: string;
};

const CandidateBox = ({
	candidate,
	className = '',
	color = '--ea-blue',
}: CandidateBoxProps) => {
	const theme = useContext(Theme);
	const { cart } = useCart();

	/**
	 * High dollar donations are a special case.
	 * Candidate amounts can be set on an individual basis.
	 */
	const {
		displayHighDollar,
		candidateAmounts,
	}:{
		displayHighDollar: boolean,
		candidateAmounts: number[]|false,
	} = useMemo(() => {
		const high = theme.donate_type === 'high';
		if (high) {
			return {
				displayHighDollar: true,
				candidateAmounts: candidate.high_dollar_donation_amounts,
			};
		}
		return {
			displayHighDollar: false,
			candidateAmounts: false,
		};
	}, [theme.donate_type, candidate]);

	return (
		<div
			className={[
				'candidate-box__wrap',
				displayHighDollar ? 'high-dollar' : '',
			].join(' ')}
		>
			<div
				className={[
					'candidate-box',
					className,
				].join(' ')}
				key={candidate.id}
				data-active={cart.selections.has(candidate.id)}
				style={{
					'--ea-auto-color': `var(${color})`,
				}}
			>
				<div className="candidate-box__inner">
					<a href={candidate.uri} target="_blank" rel="noopener noreferrer">
						{candidate.image ? (
							<Image
								{...candidate.image}
								width={300}
								height={300}
								className="candidate-box__image"
							/>
						) : (
							<div className="candidate-box__image-placeholder">
								<Logo />
							</div>
						)}
					</a>
					<div className="candidate-box__text">
						{candidate.name && (
							<h5 className="candidate-box__title">{decodeEntities(candidate.name)}</h5>
						)}
						<div className="candidate-box__tags">
							{candidate.position && candidate.position.map((p) => (
								<span key={`${p}-${candidate.id}`} className="candidate-box__tags-tag ea-tag">{decodeEntities(p)}</span>
							))}
							{candidate.state && candidate.state.map((s) => (
								<span key={`${s}-${candidate.id}`} className="candidate-box__tags-tag ea-tag">{decodeEntities(s)}</span>
							))}
						</div>
					</div>
					<div className="candidate-box__priorities">
						{candidate.race_priority && candidate.race_priority.map((p) => (
							<span key={`${p}-${candidate.id}`} className="candidate-box__priorities-tag ea-tag">{decodeEntities(p)}</span>
						))}
					</div>
					<div className="candidate-box__check">
						<CheckSimple />
					</div>
				</div>
				{cart.allowUserInput && (
					!safegaurd(candidate) ? (
						<div className="candidate-box__black-out">
							<span className="ea-small">
								{candidate.candidate_blackout_notice ? (
									candidate.candidate_blackout_notice
								) : (
									'Candidate is not accepting donations at this time.'
								)}
							</span>
						</div>
					) : (
						<AmountButtonGroup
							target={candidate}
							count={2}
							amounts={candidateAmounts}
						/>
					)
				)}
			</div>
			{displayHighDollar && candidate.contribution_limit_disclaimer && (
				<div className="candidate-box__disclaimer">
					<span className="ea-small">
						{candidate.contribution_limit_disclaimer}
					</span>
				</div>
			)}
		</div>
	);
};

export default CandidateBox;
